import React, { useMemo, useState } from 'react';
import { ChevronDown, FolderOpen, Plus, Trash2, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '@/lib/utils';

export default function ProjectSelector({
  projects = [],
  activeProjectId,
  activeProject,
  onSelect,
  onDelete,
  onNewProject,
}) {
  const [query, setQuery] = useState('');
  const active = activeProject || projects.find((p) => p.id === activeProjectId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q)
        || (p.description || '').toLowerCase().includes(q),
    );
  }, [projects, query]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 max-w-[220px] border-white/[0.08] bg-white/[0.03] px-2.5 text-xs text-zinc-200 sm:max-w-[280px]"
          data-testid="app-builder-project-selector-trigger"
        >
          <FolderOpen className="mr-1.5 h-3.5 w-3.5 shrink-0 text-sky-400" />
          <span className="truncate">{active?.name || 'Select project'}</span>
          <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-zinc-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[min(360px,calc(100vw-2rem))] border-white/[0.08] bg-[#141414] p-0 text-zinc-200"
        data-testid="app-builder-project-selector-menu"
      >
        <div className="border-b border-white/[0.06] p-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects…"
            className="w-full rounded-lg border border-white/[0.08] bg-black/40 px-3 py-2 text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-violet-500/40"
            data-testid="app-builder-project-search"
          />
        </div>
        <DropdownMenuLabel className="flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">
          <span>Your projects</span>
          <span>{projects.length}</span>
        </DropdownMenuLabel>
        <div className="max-h-64 overflow-y-auto px-1 pb-1" data-testid="app-builder-project-selector-list">
          {filtered.length ? filtered.map((item) => (
            <DropdownMenuItem
              key={item.id}
              className={cn(
                'group flex cursor-pointer items-start gap-2 rounded-lg px-2 py-2 focus:bg-white/[0.06] focus:text-white',
                activeProjectId === item.id && 'bg-sky-500/10 text-sky-100',
              )}
              onClick={() => onSelect?.(item.id)}
              data-testid={`project-selector-item-${item.id}`}
            >
              <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">{item.name}</p>
                <p className="mt-0.5 line-clamp-1 text-[10px] text-zinc-500">
                  {item.description || item.active_phase || 'Autonomous builder project'}
                </p>
              </div>
              <button
                type="button"
                className="rounded p-1 text-zinc-600 opacity-0 transition hover:text-red-300 group-hover:opacity-100"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete?.(item.id);
                }}
                data-testid={`project-selector-delete-${item.id}`}
                aria-label="Delete project"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuItem>
          )) : (
            <p className="px-3 py-4 text-center text-xs text-zinc-500">No projects match your search.</p>
          )}
        </div>
        <DropdownMenuSeparator className="bg-white/[0.06]" />
        <DropdownMenuItem
          className="mx-1 mb-1 rounded-lg text-xs focus:bg-violet-500/15 focus:text-violet-100"
          onClick={() => onNewProject?.()}
          data-testid="app-builder-project-selector-new"
        >
          <Plus className="mr-2 h-3.5 w-3.5" />
          New project
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
